import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import Chip from "@mui/material/Chip";
import { useParams } from "react-router-dom";

import AppContext from "../../../context/app-context";
import ListDividers from "./List";
import { formatIstDate, formatIstDateTime } from "../../../utils/time";

const url = process.env.REACT_APP_URL;


function DeviceDetailsCard() {
  const { siteCode } = useParams();
  const { addressData, setCoordinates, setSitess } = useContext(AppContext);
  const [deviceRow, setDeviceRow] = useState(null);
  const [latestEvent, setLatestEvent] = useState(null);
  const masterRow = useMemo(
    () => addressData?.find?.((row) => row.site_code === siteCode) || null,
    [addressData, siteCode]
  );

  useEffect(() => {
    let mounted = true;

    const loadDevice = async () => {
      try {
        const deviceResponse = await axios.get(`${url}/core_data/all/devices`);
        if (!mounted) {
          return;
        }

        const deviceRows = Array.isArray(deviceResponse.data?.data) ? deviceResponse.data.data : [];
        const device = deviceRows.find((row) => row.site_code === siteCode) || null;
        setDeviceRow(device);
      } catch (error) {
        console.error("Failed to load device details", error);
      }
    };

    loadDevice();

    return () => {
      mounted = false;
    };
  }, [siteCode]);

  useEffect(() => {
    if (masterRow?.gps_coordinates) {
      setCoordinates(masterRow.gps_coordinates);
    }
    if (masterRow?.site_code) {
      setSitess(masterRow.site_code);
    }
  }, [masterRow, setCoordinates, setSitess]);

  useEffect(() => {
    let mounted = true;
    const intervalId = setInterval(async () => {
      try {
        const eventResponse = await axios.get(`${url}/api/v1/button_pressed`);
        if (!mounted) {
          return;
        }
        const eventRows = Array.isArray(eventResponse.data?.data) ? eventResponse.data.data : [];
        setLatestEvent(masterRow?.imei ? (eventRows.find((row) => row.imei === masterRow.imei) || null) : null);
      } catch (error) {
        console.error("Failed to refresh latest event", error);
      }
    }, 2000);

    return () => {
      mounted = false;
      clearInterval(intervalId);
    };
  }, [masterRow?.imei]);

  const statusLabel = useMemo(() => {
    if (!masterRow) {
      return "Loading";
    }
    if (masterRow.emergency === "yes") {
      return "Emergency";
    }
    if (masterRow.functional === "yes") {
      return "Healthy";
    }
    return "Unhealthy";
  }, [masterRow]);

  const statusColor = useMemo(() => {
    if (!masterRow) {
      return "default";
    }
    if (masterRow.emergency === "yes") {
      return "error";
    }
    if (masterRow.functional === "yes") {
      return "success";
    }
    return "warning";
  }, [masterRow]);

  return (
    <div>
      <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
        <div>
          <div
            className="text-uppercase text-secondary"
            style={{ letterSpacing: 1.2, fontSize: 12 }}
          >
            Device Overview
          </div>
          <h3 className="mb-1 fw-bold">{masterRow?.bus_stop || "CoreData Office"}</h3>
          <div className="text-muted">
            {masterRow?.road || "Sector 62 Noida"} -{" "}
            {masterRow?.towards || "Noida Electronic City"}
          </div>
        </div>
        <Chip label={statusLabel} color={statusColor} variant="filled" sx={{ fontWeight: 700 }} />
      </div>

      <div className="row g-3">
        <div className="col-12">
          <ListDividers
            t1="Device Name:-"
            v1={deviceRow?.device_model_no || "RPI-EMERGENCY-BOX"}
            t2="Site Code:-"
            v2={siteCode}
            t3="Bus Stop:-"
            v3={masterRow?.bus_stop || "Awaiting data"}
            t4="Road:-"
            v4={masterRow?.road || "Awaiting data"}
            t5="Towards:-"
            v5={masterRow?.towards || "Awaiting data"}
            t6="Coordinates"
            v6={masterRow?.gps_coordinates || "Awaiting data"}
            t7="Installation Date"
            v7={formatIstDate(deviceRow?.ts)}
          />
        </div>

        <div className="col-12">
          <ListDividers
            t1="Current Status:-"
            v1={statusLabel}
            t2="Last Data:-"
            v2={formatIstDateTime(masterRow?.last_health_data_time)}
            t3="Data Interval:-"
            v3="Live sync"
            t4="Last Call Time:-"
            v4={formatIstDateTime(masterRow?.last_call_time)}
            t5="Last Call Mode:-"
            v5={latestEvent?.pressed || latestEvent?.event || "Awaiting event"}
            t6="Latest Event Time"
            v6={latestEvent?.timestamp ? formatIstDateTime(latestEvent.timestamp) : "Awaiting event"}
            t7="Latest Event"
            v7={latestEvent?.pressed || latestEvent?.event || "Awaiting event"}
          />
        </div>
      </div>
    </div>
  );
}

export default DeviceDetailsCard;
